import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react'

import { ApiError, api } from '../lib/api'

/**
 * One step, as the server describes it.
 *
 * **The server owns this list and the client does not keep a copy.** The six
 * slugs, their order, and which of them may be skipped all arrive from
 * `GET /api/onboarding` (`model.Steps` in the backend is the only definition).
 * SUAP-0204's fourth acceptance criterion is that the client and server agree
 * about which steps are skippable — one list is how you get that; two lists and
 * a drift check is how you find out you did not have it.
 *
 * What the client does own is the *copy*, keyed by slug: `onboarding.step.<slug>.title`
 * and `.why` in the dictionary. `check:i18n` cannot see those keys, because the
 * slug is data rather than a literal — so `STEP_COPY` in `pages/Onboarding.tsx`
 * spells them out and is `satisfies`-checked, which makes a deleted dictionary
 * entry a compile error. This comment used to say a missing entry "renders
 * untranslated"; it actually threw a `TypeError`, and nothing caught it. Both
 * were corrected at review.
 */
export interface OnboardingStep {
  slug: string
  skippable: boolean
  complete: boolean
  skipped: boolean
  reachable: boolean
}

/** The answers so far, so a step the user already filled in re-renders filled in. */
export interface OnboardingProfile {
  sex_at_birth: string | null
  birth_date: string | null
  height_cm: string | null
  activity_level: string | null
  training_style: string | null
  training_days_per_week: number | null
  timezone: string
  locale: string
  unit_system: string
  meal_pattern: string | null
}

export interface OnboardingState {
  steps: OnboardingStep[]
  /** The first unresolved step; empty once the wizard is finished. */
  current_step: string
  completed_at: string | null
  profile: OnboardingProfile
}

/** What a step body sends. Only the fields that step owns; absent is untouched. */
export type OnboardingAnswers = Record<string, unknown>

interface OnboardingValue {
  status: 'loading' | 'ready' | 'error'
  state: OnboardingState | null
  /** The last save failure, cleared by the next attempt. */
  error: ApiError | null
  save: (slug: string, answers?: OnboardingAnswers) => Promise<void>
  skip: (slug: string) => Promise<void>
  reload: () => Promise<void>
}

const OnboardingContext = createContext<OnboardingValue | null>(null)

/**
 * Wizard state, loaded from the server and written back after every step.
 *
 * **There is no local draft.** Every answer goes to the server as the step is
 * left, and the state the shell renders is the state the server returned — which
 * is the whole of "closing the app mid-wizard and returning loses nothing"
 * (AC-1) and "back navigation preserves entered answers" (AC-2). A local draft
 * would satisfy both on one device and neither on a second, and the epic's
 * outcome is a Profile on the server.
 *
 * SUAP-0204.
 */
export function OnboardingProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<OnboardingState | null>(null)
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading')
  const [error, setError] = useState<ApiError | null>(null)

  const reload = useCallback(async () => {
    try {
      setState(await api.get<OnboardingState>('/api/onboarding'))
      setStatus('ready')
    } catch (caught) {
      setError(caught as ApiError)
      setStatus('error')
    }
  }, [])

  useEffect(() => {
    void reload()
  }, [reload])

  /**
   * Write one step.
   *
   * The response is the new state, so the shell never computes what the server
   * just decided — which step is next, whether the wizard is finished, what the
   * stored (normalised) answers are. A client that recomputed any of those would
   * be a second implementation of the rule, and the two would disagree the first
   * time one changed.
   */
  const put = useCallback(async (slug: string, body: Record<string, unknown>) => {
    setError(null)
    try {
      setState(await api.put<OnboardingState>(`/api/onboarding/steps/${slug}`, body))
    } catch (caught) {
      setError(caught as ApiError)
      // Rethrown as well as stored: the caller needs to know not to navigate,
      // and swallowing it here is how a wizard advances past a step it failed to
      // save.
      throw caught
    }
  }, [])

  const save = useCallback(
    (slug: string, answers?: OnboardingAnswers) => put(slug, answers === undefined ? {} : { answers }),
    [put],
  )
  const skip = useCallback((slug: string) => put(slug, { skip: true }), [put])

  return (
    <OnboardingContext.Provider value={{ status, state, error, save, skip, reload }}>
      {children}
    </OnboardingContext.Provider>
  )
}

export function useOnboarding(): OnboardingValue {
  const value = useContext(OnboardingContext)
  if (value === null) throw new Error('useOnboarding must be used inside <OnboardingProvider>')
  return value
}
